import React, { useState } from "react";
import { motion } from "framer-motion";
import ContactModal from "../common/ContactModal";
import { assetPath } from "../../utils/assetPath";

/**
 * CTASection — Premium CTA card with background images and gradient overlay.
 *
 * Props:
 *  - title:               Heading text
 *  - description:         Subtext below the heading
 *  - buttonText:          Primary CTA button label (opens contact modal)
 *  - secondaryButtonText: Secondary button label
 *  - downloadKey:         If provided, secondary button triggers a direct PDF download
 *                         instead of opening the contact modal with "download" intent.
 *  - extraContent:        Optional JSX rendered below buttons
 */
const CTASection = ({
  title = "",
  description = "",
  buttonText = "Talk to an Expert",
  secondaryButtonText = "",
  microcopy = "",
  extraContent = null,
  downloadKey = "",
}) => {
  const [contactModalState, setContactModalState] = useState({
    isOpen: false,
    intent: "contact",
  });

  const handleSecondaryClick = () => {
    if (downloadKey) {
      setContactModalState({ isOpen: true, intent: "download" });
    } else {
      setContactModalState({ isOpen: true, intent: "download" });
    }
  };

  return (
    <section
      className="py-1 md:py-24 relative overflow-hidden"
      id="contact"
      aria-label="Call to action"
    >
      <div className="max-w-[1600px] mx-auto px-8 md:px-10 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[32px] overflow-hidden min-h-[400px] flex items-center justify-center shadow-2xl will-change-[transform,opacity]"
        >
          {/* Gradient background */}
          <div
            className="absolute inset-0 z-[1]"
            style={{
              background: "linear-gradient(180deg, #123C79 0%, #397AD9 100%)",
            }}
          />

          {/* Left image */}
          <div className="absolute left-0  w-full md:w-[85%] z-10 pointer-events-none">
            <img
              src={assetPath("/CTA/LeftPic.png")}
              alt="Decorative background pattern"
              aria-hidden="true"
              role="presentation"
              className="w-[60%]  h-[60%] object-cover object-left opacity-40"
            />
          </div>

          {/* Right image */}
          <div className="absolute right-0 top-0 bottom-0 w-full md:w-[40%] z-10 pointer-events-none">
            <img
              src={assetPath("/CTA/RightPic.png")}
              alt="Decorative background pattern"
              aria-hidden="true"
              role="presentation"
              className="w-full h-full object-cover object-left opacity-20"
            />
          </div>

          {/* Content */}
          <div className="relative z-20 flex flex-col items-center text-center px-8 md:px-16 py-16 md:py-20 max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.2] tracking-tight mb-6">
              {title}
            </h2>

            {description && (
              <p className="text-white/80 text-sm md:text-base mb-10 max-w-xl mx-auto leading-relaxed">
                {description}
              </p>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row w-full sm:w-auto items-center justify-center gap-4 mb-6">
              {secondaryButtonText && (
                <button
                  onClick={handleSecondaryClick}
                  className="w-full sm:w-auto px-6 md:px-8 py-3.5 bg-transparent text-white font-bold text-[13px] md:text-sm rounded-full
                             border-2 border-white/40 hover:bg-white/10 hover:border-white/60
                             transition-all duration-300 cursor-pointer leading-tight sm:whitespace-nowrap"
                >
                  {secondaryButtonText}
                </button>
              )}
              {buttonText && (
                <button
                  onClick={() =>
                    setContactModalState({ isOpen: true, intent: "contact" })
                  }
                  aria-label={`Contact us: ${buttonText}`}
                  className="w-full sm:w-auto px-6 md:px-8 py-3.5 bg-white text-[#003EC7] font-bold text-[13px] md:text-sm rounded-full
                             hover:bg-white/90 transition-all duration-300 cursor-pointer
                             shadow-[0_4px_20px_rgba(0,0,0,0.15)] leading-tight sm:whitespace-nowrap"
                >
                  {buttonText}
                </button>
              )}
            </div>

            {/* Footer microcopy */}
            {microcopy && (
              <p className="text-white/50 text-xs font-medium max-w-xl mx-auto">
                {microcopy}
              </p>
            )}

            {extraContent && (
              <div className="mt-8 w-full flex justify-center">
                {extraContent}
              </div>
            )}
          </div>
        </motion.div>

        <ContactModal
          isOpen={contactModalState.isOpen}
          intent={contactModalState.intent}
          downloadKey={downloadKey}
          onClose={() =>
            setContactModalState((prev) => ({ ...prev, isOpen: false }))
          }
        />
      </div>
    </section>
  );
};

export default CTASection;
